import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { Button, Input, useToast } from '@shared/ui';
import { validateEmail } from '@entities/auth';
import { useForgotPassword } from '@features/auth/forgot-password';
import { useCooldown } from '@entities/auth';
import { passwordErrorMessage } from '@entities/auth';

const RESEND_COOLDOWN_SEC = 60;

interface ForgotPasswordPanelProps {
  onBack: () => void;
}

export function ForgotPasswordPanel({ onBack }: ForgotPasswordPanelProps) {
  const { t, i18n } = useTranslation('auth');
  const { toast } = useToast();
  const { requestLink, isLoading } = useForgotPassword();
  const { secondsLeft, start } = useCooldown();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t('recovery.forgotTitle');
    return () => {
      document.title = previousTitle;
    };
  }, [t]);

  const submit = async () => {
    const validation = validateEmail(email);
    if (!validation.isValid && validation.error) {
      setError(t(validation.error));
      return;
    }

    setError('');
    const result = await requestLink(email.trim());
    if (result.success) {
      setSent(true);
      start(RESEND_COOLDOWN_SEC);
      return;
    }

    if (result.failure?.code === 'TOO_MANY_REQUESTS') {
      start(result.failure.retryAfterSec ?? RESEND_COOLDOWN_SEC);
      const message = passwordErrorMessage(result.failure, t, i18n.language);
      if (message) {
        toast(message, 'error');
      }
      return;
    }

    if (result.failure?.fieldErrors.email) {
      setError(t(result.failure.fieldErrors.email));
      return;
    }

    toast(t('recovery.requestFailed'), 'error');
  };

  const buttonLabel =
    secondsLeft > 0
      ? t('recovery.sendAgainIn', { seconds: secondsLeft })
      : sent
        ? t('recovery.sendAgain')
        : t('recovery.sendLink');

  return (
    <>
      <div className="w-full space-y-3 text-center lg:text-left">
        <h2 className="auth-panel-title">{t('recovery.forgotTitle')}</h2>
        <p className="auth-panel-description">{t('recovery.forgotDescription')}</p>
      </div>

      {sent && <p className="w-full text-sm text-foreground">{t('recovery.forgotSent')}</p>}

      <form
        noValidate
        className="flex w-full flex-col items-center gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Input
          type="email"
          autoComplete="email"
          placeholder={t('email')}
          value={email}
          disabled={isLoading}
          error={error}
          icon={<Mail className="h-4 w-4" />}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button type="submit" variant="default" className="w-full" loading={isLoading} disabled={secondsLeft > 0}>
          {buttonLabel}
        </Button>
      </form>

      <button
        type="button"
        className="text-sm text-muted-foreground underline transition-colors hover:text-foreground"
        onClick={onBack}
      >
        {t('backToSignIn')}
      </button>
    </>
  );
}
